// S5 · Step 3 — plant map i18n (`onboarding.plant.*`). Composed into the
// onboarding namespace in `onboarding.i18n.ts`, so the four locales stay in KEY
// parity through the CI gate (`pnpm run i18n:parity`, D-014). ru is TRANSLATED
// (plant-map.ru.ts); zh/fr still render English — see src/i18n-completeness.ts.
//
// Entity NOUNS are NOT written here: facility / department / work center /
// warehouse / machine all resolve at runtime through `useTerm()` so a tenant's
// own vocabulary wins (§2.5). Copy in this file is therefore phrased so the
// noun can be interpolated ({{facility}}, {{department}}, …).

export const plantMapEn = {
  intro: "Lay out your plant as a tree, then commit it.",

  // ---- the tree editor -----------------------------------------------------
  treeHeading: "Plant tree",
  addFacility: "Add {{facility}}",
  removeFacility: "Remove {{facility}}",
  addDepartment: "Add {{department}}",
  removeDepartment: "Remove {{department}}",
  addWorkCenter: "Add {{workCenter}}",
  removeWorkCenter: "Remove {{workCenter}}",
  moveUp: "Move up",
  moveDown: "Move down",
  removeConfirm: "Remove this from the draft? It is only removed from your draft until you commit.",

  facilityLabel: "{{facility}} {{number}}",
  departmentsHeading: "{{department}} list",
  workCentersHeading: "{{workCenter}} list",
  storageHeading: "Storage locations",
  addStorage: "Add storage location",
  removeStorage: "Remove storage location",

  codeLabel: "Code",
  nameLabel: "Name",
  timezoneLabel: "Time zone",
  addressLabel: "Address",
  addressHint: "Leave blank to keep whatever address is already recorded.",
  templateLabel: "Template",
  templateNone: "No template",
  templateNoPack: "Install an industry pack to get templates.",
  machineCountLabel: "{{machine}} count",
  machineCountHint:
    "Generates {{machine}} codes {{example}} on the first commit, and only while this {{workCenter}} has none.",
  parentLabel: "Inside",
  parentNone: "Top level",
  storageHint:
    "Warehouses, racks and zones. Opening stock and location labels resolve against these codes.",
  codeHint:
    "Short, stable, unique. The commit matches by code — renaming a code creates a new one.",

  // ---- empty / seeding -----------------------------------------------------
  emptyTitle: "Map your plant",
  emptyBody: "Add your first {{facility}}, then its {{department}} list and {{workCenter}} list.",
  emptyCta: "Add the first {{facility}}",
  seedTitle: "You already have a committed plant",
  seedBody: "Load the committed tree into your draft so you edit what the factory actually has.",
  seedCta: "Load committed tree",
  seedNote:
    "Storage locations and {{machine}} counts are not loaded into this tree yet, so it shows only what can be read today.",

  // ---- validation (client mirror of 422 DUPLICATE_CODE) --------------------
  problemsTitle: "Fix these before committing",
  warningsTitle: "Worth checking",
  problem: {
    facility_code_required: "{{path}} — a {{facility}} needs a code.",
    facility_name_required: "{{path}} — a {{facility}} needs a name.",
    facility_timezone_required: "{{path}} — a {{facility}} needs a time zone.",
    department_code_required: "{{path}} — a {{department}} needs a code.",
    department_name_required: "{{path}} — a {{department}} needs a name.",
    work_center_code_required: "{{path}} — a {{workCenter}} needs a code.",
    work_center_name_required: "{{path}} — a {{workCenter}} needs a name.",
    storage_code_required: "{{path}} — a storage location needs a code.",
    storage_name_required: "{{path}} — a storage location needs a name.",
    duplicate_facility_code: "{{path}} — this {{facility}} code is used twice.",
    duplicate_department_code:
      "{{path}} — this {{department}} code is used twice in the {{facility}}.",
    duplicate_work_center_code:
      "{{path}} — this {{workCenter}} code is used twice in the {{facility}}. Codes are unique per {{facility}}, across every {{department}}.",
    duplicate_storage_code:
      "{{path}} — this storage location code is used twice in the {{facility}}.",
    unknown_storage_parent:
      "{{path}} — no storage location in this {{facility}} has that parent code.",
    work_center_moved:
      "{{path}} — this {{workCenter}} already exists in another {{department}}. Committing updates its name, but does not move it.",
  },

  // ---- §3.7 structure editor (UX-1) ----------------------------------------
  draftChip: "draft",
  toggleNode: "Expand or collapse {{node}}",
  wcMachinesMeta: "{{count}} {{machine}}",
  ghostAddWorkCenter: "+ Add {{workCenter}} to {{department}}",
  ghostAddDepartment: "+ Add {{department}} to {{facility}}",
  ghostAddStorage: "+ Add storage location to {{facility}}",
  storageMeta: "storage",
  removeFromDraft: "Remove {{node}} from the draft…",
  // PLURALS. i18next resolves `key_<category>` first and falls back to the BARE
  // key (translator.js builds `[key, key+suffix]` and pops from the end), so the
  // convention here is: the bare key carries the general form and a `_one`
  // sibling carries the singular. English needs exactly those two; Russian's
  // few/many categories fall back to the bare key, which the ru file writes
  // count-last so it is grammatical for every count. `plant-map.plurals.test.ts`
  // is the gate: a `{{count}}` key must be plural-formed or declared invariant.
  nodeCount: "{{count}} nodes · {{draft}} draft",
  nodeCount_one: "{{count}} node · {{draft}} draft",
  propsEmptyTitle: "Properties",
  propsEmptyBody: "Select a node in the tree to edit its details here.",

  // ---- the live commit sentence (§3.7 CommitBar) ---------------------------
  sentence: {
    create: "This commit will create {{parts}}.",
    facilities: "{{count}} {{facility}} ({{names}})",
    departments: "{{count}} {{department}} ({{names}})",
    workCenters: "{{count}} {{workCenter}} ({{names}})",
    storage: "{{count}} storage locations",
    storage_one: "{{count}} storage location",
    // Its OWN sentence, not a `create` part: §3.4's server un-archives a draft
    // node whose code belongs to an archived row and counts it under `updated`,
    // never under `created`, so saying "create" about it would be the lie F2
    // was about.
    restore: "{{count}} archived nodes are restored, not created: {{names}}.",
    restore_one: "{{count}} archived node is restored, not created: {{names}}.",
    // Also its own sentence, and also counted under `updated` by the server. It
    // is the only warning before a committed name is overwritten — while the
    // diff matched by code alone, a rename scored as "nothing to commit" and
    // could not be committed at all.
    edited: "{{count}} existing nodes are updated in place: {{names}}.",
    edited_one: "{{count}} existing node is updated in place: {{names}}.",
    matched: "{{count}} existing nodes are matched by code and left unchanged.",
    matched_one: "{{count}} existing node is matched by code and left unchanged.",
    onlyDeletions:
      "This commit creates nothing. It only archives the {{count}} committed nodes missing from your draft, and only if you confirm below.",
    onlyDeletions_one:
      "This commit creates nothing. It only archives the one committed node missing from your draft, and only if you confirm below.",
    comma: ", ",
    and: " and ",
  },
  commitValidated: "Validated · no conflicts",
  discardDraft: "Discard draft",

  // ---- state 4 · ONE success record (§3.8, §UX-1 "toast-scale") ------------
  committedTitle: "Plant map committed.",
  // The reference's own headline for this state: "Committed 3 nodes — X, Y, Z."
  // The COUNT is the server's; the names are shown only when it agrees.
  committedNodes: "Committed {{count}} nodes —",
  committedNodes_one: "Committed {{count}} node —",
  committedCount: "Committed {{count}} nodes.",
  committedCount_one: "Committed {{count}} node.",
  restoredNodes: "Restored from archived:",
  setupDelta: "Setup +{{delta}} → {{score}}",
  setupScore: "Setup {{score}}",
  dismissBanner: "Dismiss",
  continueNext: "Continue: {{step}}",
  resultPart: "{{count}} × {{entity}}",
  resultCreatedLine: "Created {{parts}}.",
  resultUpdatedLine: "Updated {{updated}} · archived {{deleted}}.",

  // ---- state 5 · follow-up work, disclosed (phone) -------------------------
  followUpTitle: "Bind steps and invite people",
  followUpHint: "Work on the committed plant",

  // ---- diff summary --------------------------------------------------------
  diffHeading: "What Commit will do",
  diffCreate: "Create",
  diffAdopt: "Match existing",
  diffFacilities: "{{facility}} list",
  diffDepartments: "{{department}} list",
  diffWorkCenters: "{{workCenter}} list",
  diffStorage: "Storage locations",
  diffMachines: "{{machine}} list",
  diffNothing: "Nothing to commit yet.",
  diffUpdateNote: "An entry that already exists is updated only if its name or time zone changed.",

  // ---- deletions (§3.4: never implicit) ------------------------------------
  absentTitle: "Committed nodes missing from your draft",
  absentBody:
    "These exist in your factory but are not in this draft. By default Commit leaves them alone.",
  absentKind: {
    facility: "{{facility}}",
    department: "{{department}}",
    work_center: "{{workCenter}}",
  },
  confirmDeletionsLabel: "Archive the {{count}} missing nodes when I commit",
  confirmDeletionsLabel_one: "Archive the missing node when I commit",
  confirmDeletionsHint:
    "Archiving is a real change. A node that still has children or members is refused, not archived.",
  deleteConfirmTitle: "Archive {{count}} committed nodes?",
  deleteConfirmTitle_one: "Archive this committed node?",
  deleteConfirmBody: "Saving archives these. If any of them is still in use, nothing is archived.",
  deleteConfirmCta: "Yes, commit and archive",
  cancel: "Cancel",

  // ---- committed-tree read -------------------------------------------------
  existingLoading: "Reading your committed plant…",
  existingForbiddenTitle: "Committed plant not visible to you",
  existingForbiddenBody:
    "Your role cannot read the org tree, so this screen cannot show what already exists — and will not offer to archive anything.",
  existingErrorTitle: "Could not read your committed plant",
  existingErrorBody: "The diff summary and the deletion list are unavailable until this loads.",

  // ---- commit --------------------------------------------------------------
  commitHeading: "Commit the plant map",
  commitCta: "Commit plant map",
  commitWorking: "Committing…",
  commitBlocked: "Fix the problems above before committing.",
  commitReadOnly: "You do not have permission to commit the plant map.",
  commitFrozen: "Your factory is live. The plant map is kept as a record of the setup.",
  commitSaveFirst: "Commit sends the draft as it is on screen. Save it too, so a refresh keeps it.",

  resultCreated: "Created",
  resultUpdated: "Updated",
  resultDeleted: "Archived",
  resultNoChange: "Nothing changed — your draft already matches the committed plant.",
  resultFacilities: "{{facility}} list",
  resultDepartments: "{{department}} list",
  resultWorkCenters: "{{workCenter}} list",
  resultStorage: "Storage locations",
  resultMachines: "{{machine}} list",

  pendingEntity: {
    storage_locations: "Storage locations",
    machines: "{{machine}} list",
  },
  // One sentence inside the banner (§UX-1 "one announcement"), still carrying
  // the honest "recorded, not created yet" the §3.4 pending[] exists to say.
  pendingLine: "Recorded, not created yet — {{entity}}: {{count}}, waiting on {{module}}.",

  // ---- commit failures -----------------------------------------------------
  duplicateTitle: "Duplicate code in the draft",
  duplicateBody:
    "Two entries share a code. Codes must be unique: a {{facility}} across the company, a {{department}} and a {{workCenter}} within one {{facility}}.",
  refusedTitle: "Some deletions were refused",
  refusedBody:
    "Nothing was archived. Each node below is still referenced — clear it, or put it back in your draft.",
  refusedReasonLabel: "Reason given",
  commitFailedTitle: "The plant map was not committed",
  commitFailedDetail: "Nothing was changed. Try again.",

  // ---- destructive draft edits (§3.4 "deletions are never implicit") --------
  removeConfirmTitle: "Remove {{node}}?",
  removeConfirmBody:
    "{{node}} is removed from your draft along with {{summary}}. Nothing that has already been committed is deleted by this.",
  removeConfirmCta: "Remove it",
  removeConfirmUndoHint: "You can put it back with Undo straight afterwards.",
  removeCount: {
    departments: "{{count}} {{department}}",
    workCenters: "{{count}} {{workCenter}}",
    storage: "{{count}} storage locations",
    storage_one: "{{count}} storage location",
    machines: "{{count}} {{machine}}",
  },
  removedNotice: "Removed {{node}} from your draft.",
  undoRemove: "Undo",

  // ---- collapse + bulk add (scale, §3.7) -----------------------------------
  facilitySummary: "{{departments}} {{department}} · {{workCenters}} {{workCenter}}",
  departmentSummary: "{{workCenters}} {{workCenter}} · {{machines}} {{machine}}",
  bulkAddLabel: "Add several {{workCenter}}",
  bulkAddCta: "Add {{count}}",

  // ---- honesty about deferred ports ----------------------------------------
  diffDeferredHeading: "Recorded, not created yet",
  diffDeferredEntry: "{{entity}}: {{count}} — waiting on {{module}}",
  machineCountDeferred: "Entered by hand.",
  refusedReasonGeneric:
    "This node is still in use elsewhere, so it was not archived. Nothing was changed.",

  // ---- S5 "Bind work centers" (§4 S5) --------------------------------------
  bindings: {
    heading: "Bind your work centers",
    blurb: "Point each pack step at a real {{workCenter}}. Go-live needs this.",
    facilityLabel: "{{facility}}",
    loading: "Loading your bindings…",
    errorTitle: "Could not load the bindings",
    errorBody: "The bindings could not be loaded. Try again.",
    noPackTitle: "Choose an industry pack first",
    noPackBody: "Bindings connect the pack's steps to your work centers, so the pack comes first.",
    commitFirstTitle: "Commit your plant map first",
    commitFirstBody:
      "Bindings point at real {{facility}} records, so commit the map above and they appear here.",
    noTemplatesTitle: "This pack defines no steps",
    noTemplatesBody: "There is nothing to bind. This check will pass on its own.",
    noWorkCentersTitle: "No {{workCenter}} yet",
    noWorkCentersBody:
      "Add and commit at least one {{workCenter}} in this facility, then come back to bind the steps.",
    unbound: "Not bound",
    unboundBadge: "Not bound",
    boundBadge: "Bound",
    summary: "{{bound}} of {{total}} steps bound",
    unboundWarning: "{{count}} steps are not bound yet. Go-live is blocked until they are.",
    unboundWarning_one: "{{count}} step is not bound yet. Go-live is blocked until it is.",
    save: "Save the bindings",
    saving: "Saving…",
    saveFailedTitle: "Could not save the bindings",
    saveFailedBody: "Nothing was changed. Try again.",
    replaceNote:
      "Saving replaces the whole set for this facility — anything left unbound is unbound.",
    readOnly: "Your role cannot change bindings.",
  },

  // ---- S5 People panel (§4 S5) ---------------------------------------------
  people: {
    heading: "People",
    blurb: "Invite the admins, planners and floor workers who run this factory.",
    bulkHint: "Have a list already?",
    bulkCta: "Import people from a spreadsheet",
    notPermitted: "Your role cannot invite people. Ask a group admin.",
    emailLabel: "Email",
    roleLabel: "Role",
    rolePlaceholder: "Choose a role…",
    scopeLabel: "Access",
    scopeGroup: "The whole group",
    scopeFacility: "One {{facility}}",
    facilityLabel: "{{facility}}",
    facilityPlaceholder: "Choose one…",
    commitFirst: "Commit your plant map to create the {{facility}} first.",
    invite: "Send the invite",
    inviting: "Sending…",
    inviteFailedTitle: "Could not send that invite",
    inviteFailedBody: "Nobody was invited. Check the address and try again.",
    loading: "Loading invites…",
    errorTitle: "Could not load the invites",
    errorBody: "The invite list could not be loaded. Try again.",
    emptyTitle: "No invites outstanding",
    emptyBody: "Nobody has been invited yet. Invite your facility admins to get started.",
    colEmail: "Email",
    colScope: "Access",
    colExpires: "Expires",
    colActions: "Actions",
    scope: {
      group: "Whole group",
      facility: "One facility",
      department: "One department",
    },
    resend: "Resend",
    resendCapped: "This invite has been resent the maximum number of times.",
    revoke: "Revoke",
    revokeConfirmTitle: "Revoke this invite?",
    revokeConfirmBody:
      "{{email}} will not be able to use the link they were sent. You can invite them again afterwards.",
  },
} as const;
